import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import LockIcon from '@mui/icons-material/Lock';

const EncryptionSettings = () => {
  const [encryptionEnabled, setEncryptionEnabled] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load encryption settings on component mount
  useEffect(() => {
    const loadEncryptionSettings = () => {
      try {
        const settings = localStorage.getItem('encryptionSettings');
        if (settings) {
          const parsed = JSON.parse(settings);
          setEncryptionEnabled(parsed.enabled || false);
        }
      } catch (error) {
        console.error('Error loading encryption settings:', error);
      }
    };

    loadEncryptionSettings();
  }, []);

  const handleEncryptionToggle = (event) => {
    const enabled = event.target.checked;
    
    if (enabled) {
      // Show password dialog to enable encryption
      setShowPasswordDialog(true);
    } else {
      // Disable encryption immediately
      setEncryptionEnabled(false);
      saveEncryptionSettings(false);
      setSuccess('Encryption disabled successfully');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handlePasswordSubmit = async () => {
    setError('');
    
    // Validate passwords
    if (!password || password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsLoading(true);
      
      // Simulate encryption setup (in a real app, this would generate encryption keys)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Save encryption settings
      setEncryptionEnabled(true);
      saveEncryptionSettings(true, password);
      
      // Close dialog and show success
      setShowPasswordDialog(false);
      setPassword('');
      setConfirmPassword('');
      setSuccess('Encryption enabled successfully');
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      setError('Failed to enable encryption. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const saveEncryptionSettings = (enabled, encryptionPassword = null) => {
    try {
      const settings = {
        enabled,
        timestamp: new Date().toISOString()
      };
      
      // In a real app, you would securely store the encryption key
      // For demo purposes, we just store the enabled state
      localStorage.setItem('encryptionSettings', JSON.stringify(settings));
      
      if (encryptionPassword) {
        // In production, this would be used to derive encryption keys
        console.log('Encryption password set (not stored in localStorage for security)');
      }
    } catch (error) {
      console.error('Error saving encryption settings:', error);
    }
  };

  const handleDialogClose = () => {
    setShowPasswordDialog(false);
    setPassword('');
    setConfirmPassword('');
    setError('');
  };

  return (
    <Box>
      {/* Status Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Encryption Status */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {encryptionEnabled ? (
          <LockIcon color="success" sx={{ mr: 1 }} />
        ) : (
          <SecurityIcon color="disabled" sx={{ mr: 1 }} />
        )}
        <Typography variant="body1" sx={{ flexGrow: 1 }}>
          Data Encryption: {encryptionEnabled ? 'Enabled' : 'Disabled'}
        </Typography>
      </Box>

      {/* Description */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        When enabled, your data will be encrypted locally before being stored. 
        This provides an additional layer of security for your personal information.
      </Typography>

      {/* Encryption Toggle */}
      <FormControlLabel
        control={
          <Switch
            checked={encryptionEnabled}
            onChange={handleEncryptionToggle}
            color="primary"
          />
        }
        label="Enable Local Data Encryption"
      />

      {/* Current Status Info */}
      {encryptionEnabled && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Encryption is active.</strong> Your data is being encrypted locally 
            before storage. Keep your encryption password safe - it cannot be recovered if lost.
          </Typography>
        </Alert>
      )}

      {/* Password Setup Dialog */}
      <Dialog open={showPasswordDialog} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1 }} />
            Set Encryption Password
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Choose a strong password to encrypt your data. This password will be required 
            to decrypt your data. <strong>Important:</strong> If you forget this password, 
            your encrypted data cannot be recovered.
          </DialogContentText>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            fullWidth
            type="password"
            label="Encryption Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            disabled={isLoading}
            helperText="Minimum 8 characters"
          />
          
          <TextField
            fullWidth
            type="password"
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            disabled={isLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handlePasswordSubmit} 
            variant="contained" 
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <SecurityIcon />}
          >
            {isLoading ? 'Setting up...' : 'Enable Encryption'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EncryptionSettings;
